import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import { FiCheckCircle, FiDownload, FiCreditCard, FiMoreVertical } from 'react-icons/fi';
import { FaCcVisa } from 'react-icons/fa';
import useApi from '../hooks/useApi';
import { logger } from '../utils/logger';
import { API_URL } from '../config/constants';

const BillingPage = () => {
  // Fetch real data from API
  const { data: apiData, loading, error } = useApi('billing');
  
  // Initialize with empty data - will be populated from API
  const [billingData, setBillingData] = useState({
    currentPlan: {
      name: 'Loading...',
      price: '$0',
      period: 'month',
      renewalDate: 'Loading...',
      features: []
    },
    paymentHistory: [],
    paymentMethods: []
  });
  
  useEffect(() => {
    // Use real data from API
    if (apiData) {
      logger.debug('[OK] Using real billing data from backend:', apiData);
      
      setBillingData({
        currentPlan: apiData.currentPlan || {
          name: 'Free Plan',
          price: '₹0',
          period: 'month',
          renewalDate: 'No renewal',
          features: ['Basic access']
        },
        paymentHistory: apiData.paymentHistory || apiData.invoices || [],
        paymentMethods: apiData.paymentMethods || [],
        isRealData: true,
        availablePlans: apiData.availablePlans || []
      });
    }
  }, [apiData]);
  
  const { currentPlan, paymentHistory, paymentMethods } = billingData;

  if (loading) {
    return (
      <div className="flex bg-gray-50 min-h-screen font-sans relative">
        <div className="fixed left-0 top-0 bottom-0 z-10">
          <Sidebar />
        </div>
        <main className="flex-1 p-8 ml-0 md:ml-24">
          <DashboardHeader />
          <div className="mt-8">
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading billing data...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex bg-gray-50 min-h-screen font-sans relative">
      <div className="fixed left-0 top-0 bottom-0 z-10">
        <Sidebar />
      </div>
      <main className="flex-1 p-8 ml-0 md:ml-24">
        <DashboardHeader />
        <div className="mt-8">
          <h1 className="text-2xl font-bold text-black mb-6">Payments & Billing</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-8">
              <CurrentPlan plan={currentPlan} />
              {paymentHistory && paymentHistory.length > 0 && (
                <PaymentHistory history={paymentHistory} />
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-8">
              <PaymentMethods methods={paymentMethods} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const CurrentPlan = ({ plan }) => {
  const navigate = useNavigate();
  
  // Handle change plan - navigate to pricing page
  const handleChangePlan = () => {
    navigate('/pricing');
  };
  
  return (
    <div className="bg-white p-6 border border-black">
      <h3 className="text-lg font-bold text-black mb-4">Current Subscription</h3>
      <div className="bg-white p-6 border border-black">
        <div className="flex justify-between items-start">
          <div>
            <h4 className="text-xl font-bold text-black">{plan?.name || 'Free Plan'}</h4>
            <p className="text-3xl font-extrabold text-black mt-2">{plan?.price || '₹0'}<span className="text-base font-medium">/{plan?.period || 'month'}</span></p>
          </div>
          <button 
            onClick={handleChangePlan}
            className="bg-black text-white font-semibold py-2 px-4 border border-black hover:bg-gray-900"
          >
            Change Plan
          </button>
        </div>
        <div className="text-sm text-black mt-2">
          <p>Your plan renews on <strong>{plan?.renewalDate || 'No renewal'}</strong>.</p>
          {plan?.daysUntilRenewal && (
            <p className="mt-1">
              <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                {plan.daysUntilRenewal} days until next billing
              </span>
            </p>
          )}
          {plan?.subscriptionDate && (
            <p className="mt-1 text-xs text-gray-500">
              Subscribed on: {new Date(plan.subscriptionDate).toLocaleDateString()}
            </p>
          )}
        </div>
        <div className="mt-6 space-y-2">
          {(plan?.features || ['Basic access']).map((feature, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-black">
              <FiCheckCircle className="text-black" />
              <span>{feature}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PaymentHistory = ({ history }) => {
  const handleDownloadInvoice = async (transactionId) => {
    try {
      logger.debug('Downloading invoice for transaction:', transactionId);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/billing/invoice/${transactionId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download invoice');
      }
      
      // Get the filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'invoice.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      logger.debug('✅ Invoice downloaded successfully');
    } catch (error) {
      logger.error('Failed to download invoice:', error);
      alert('Failed to download invoice. Please try again.');
    }
  };
  
  return (
    <div className="bg-white p-6 border border-black">
      <h3 className="text-lg font-bold text-black mb-4">Payment History</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left border-b border-black">
              <th className="p-3 font-semibold text-black">Date</th>
              <th className="p-3 font-semibold text-black">Amount</th>
              <th className="p-3 font-semibold text-black">Status</th>
              <th className="p-3 font-semibold text-black text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {history.map((item, index) => (
              <tr key={index} className="border-b border-gray-200">
                <td className="p-3 text-black">{item.date}</td>
                <td className="p-3 text-black font-semibold">{item.amount}</td>
                <td className="p-3"><span className="bg-white text-black text-xs font-medium px-2 py-1 border border-black">{item.status}</span></td>
                <td className="p-3 text-right">
                  <button 
                    onClick={() => handleDownloadInvoice(item.id || item.orderId)}
                    className="text-black hover:underline flex items-center gap-1 ml-auto"
                  >
                    <FiDownload size={14} /> Invoice
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PaymentMethods = ({ methods }) => {
  const navigate = useNavigate();
  
  const handleAddPaymentMethod = () => {
    // Navigate to pricing page to make a payment (which will save the payment method)
    navigate('/pricing');
  };
  
  return (
    <div className="bg-white p-6 border border-black">
      <h3 className="text-lg font-bold text-black mb-4">Payment Methods</h3>
      <div className="space-y-4">
        {methods && methods.length > 0 ? (
          methods.map(method => (
            <div key={method.id} className="border border-black p-4 flex justify-between items-center">
              <div className="flex items-center gap-3">
                {method.type === 'Credit Card' ? <FaCcVisa className="text-black text-2xl" /> : <FiCreditCard className="text-black text-2xl" />}
                <div>
                  <p className="font-semibold text-black">{method.details}</p>
                  {method.isDefault && <span className="text-xs text-black border-b border-black">Default</span>}
                </div>
              </div>
              <button className="text-black"><FiMoreVertical /></button>
            </div>
          ))
        ) : (
          <p className="text-gray-500 text-center py-4">No payment methods saved yet</p>
        )}
      </div>
      <button 
        onClick={handleAddPaymentMethod}
        className="w-full mt-6 bg-black text-white font-semibold py-2 border border-black hover:bg-gray-900"
      >
        Add New Method
      </button>
    </div>
  );
};

export default BillingPage;
